import { AnyAction } from 'redux';
import { Injectable } from '@angular/core';
import {
  GlobalAction,
  RbdChanged,
  Treuebonus,
  TreuebonusChanged,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import {
  TaasNachlassVorteilRequest,
  NachlaesseForAngebot,
  Vermittlernummer,
  AgencyFinanceAccount,
} from '@ps20/bff-models';
import { NewsItem } from '../app/servicebar/news-modal/news-modal.interface';
import { VorliegendeKuendigungChanged } from './interfaces/vorliegendeKuendigung';
import { RBD } from './interfaces/rbd';
import { VnRoleEnum } from './interfaces/versicherungsnehmer';
import {
  ChangeVorteilsrabattForProduktPayload,
  VorteilsrabattProdukt,
} from './interfaces/vorteilsrabatt';

export interface ToolbarAction<T> extends AnyAction {
  payload?: T;
}

@Injectable()
export class ToolbarActions {
  static readonly SPEICHER_STATUS_CHANGED = 'SPEICHER_STATUS_CHANGED';
  static readonly VERMERK_CHANGED = 'VERMERK_CHANGED';
  static readonly OPEN_FAQ = 'OPEN_FAQ';
  static readonly GESAMTPREIS_POPOVER_OPEN_CHANGED = 'GESAMTPREIS_POPOVER_OPEN_CHANGED';
  static readonly ZAHLUNG_TOOLTIP_TOGGLE = 'ZAHLUNG_TOOLTIP_TOGGLE';
  static readonly VN_TOOLTIP_TOGGLE = 'VN_TOOLTIP_TOGGLE';
  static readonly RBD_MODAL_TOGGLE = 'RBD_MODAL_TOGGLE';
  static readonly VEREINBARUNGEN_UEBERNEHMEN = 'VEREINBARUNGEN_UEBERNEHMEN';
  static readonly RBD_CHANGED_TEMP = 'RBD_CHANGED_TEMP';
  static readonly RBD_MAX_UPDATED = 'RBD_MAX_UPDATED';
  static readonly RABATT_SERVICE_NACHLAESSE_RECEIVED = 'RABATT_SERVICE_NACHLAESSE_RECEIVED';
  static readonly RABATT_SERVICE_NACHLAESSE_SUCCESS = 'RABATT_SERVICE_NACHLAESSE_SUCCESS';
  static readonly TREUEBONUS_MAX_UPDATED = 'TREUEBONUS_MAX_UPDATED';
  static readonly TREUEBONUS_CHANGED_TEMP = 'TREUEBONUS_CHANGED_TEMP';
  static readonly KUENDIGUNG_CHANGED = 'KUENDIGUNG_CHANGED';
  static readonly TREUEBONUS_RESET_CHANGED = 'TREUEBONUS_RESET_CHANGED';
  static readonly VN_ROLE_SELECTION_CHANGED = 'VN_ROLE_SELECTION_CHANGED';
  static readonly REQUEST_LOGOUT = 'REQUEST_LOGOUT';
  static readonly UPDATE_NEWS_NEUSTE_DATE = 'UPDATE_NEWS_NEUSTE_DATE';
  static readonly UPDATE_NEWS_NEUSTE_READ = 'UPDATE_NEWS_NEUSTE_READ';
  static readonly UPDATE_CURRENT_VN_ROLE = 'UPDATE_CURRENT_VN_ROLE';
  static readonly UPDATE_VORBELEGTE_VN_ROLE = 'UPDATE_VORBELEGTE_VN_ROLE';
  static readonly INIT_MANDATORY_NEWS = 'INIT_MANDATORY_NEWS';
  static readonly NEWS_ITEM_CLICKED = 'NEWS_ITEM_CLICKED';
  static readonly SWITCH_SHOW_KONTOBEWEGUNGEN = 'SWITCH_SHOW_KONTOBEWEGUNGEN';
  static readonly CHANGE_VORTEILSRABATT_FOR_PRODUKT = 'CHANGE_VORTEILSRABATT_FOR_PRODUKT';
  static readonly UPDATE_TARIFF_UPDATE_LATEST_DATE = 'UPDATE_TARIFF_UPDATE_LATEST_DATE';
  static readonly UPDATE_TARIFF_UPDATE_LAST_READ = 'UPDATE_TARIFF_UPDATE_LAST_READ';
  static readonly RABATT_SERVICE = 'RABATT_SERVICE';
  static readonly SAVE_AS_COPY_SELECTED = 'SAVE_AS_COPY_SELECTED';
  static readonly CALL_KONTAFLEX = 'CALL_KONTAFLEX';
  static readonly KONTAFLEX_RECEIVED = 'KONTAFLEX_RECEIVED';

  speicherStatusChanged(speicherstatus: string): AnyAction {
    return {
      type: ToolbarActions.SPEICHER_STATUS_CHANGED,
      payload: speicherstatus,
    };
  }

  vermerkChanged(vermerk: string): AnyAction {
    return { type: ToolbarActions.VERMERK_CHANGED, payload: vermerk };
  }

  saveAsCopySelectionChanged(saveAsCopy: boolean): AnyAction {
    return { type: ToolbarActions.SAVE_AS_COPY_SELECTED, payload: saveAsCopy };
  }

  gesamtpreisPopupOpenChanged(gesamtpreisInfoChanged: boolean): AnyAction {
    return {
      type: ToolbarActions.GESAMTPREIS_POPOVER_OPEN_CHANGED,
      payload: gesamtpreisInfoChanged,
    };
  }

  zahlungTooltipToggle(): AnyAction {
    return { type: ToolbarActions.ZAHLUNG_TOOLTIP_TOGGLE };
  }

  vnTooltipToggle(): AnyAction {
    return { type: ToolbarActions.VN_TOOLTIP_TOGGLE };
  }

  rbdModalToggle(open: boolean): ToolbarAction<boolean> {
    return { type: ToolbarActions.RBD_MODAL_TOGGLE, payload: open };
  }

  vereinbarungenUebernehmen(): AnyAction {
    return { type: ToolbarActions.VEREINBARUNGEN_UEBERNEHMEN };
  }

  rbdChangedTemp(rbd: number, appId: App, angebotsId?: string): ToolbarAction<RbdChanged> {
    return {
      type: ToolbarActions.RBD_CHANGED_TEMP,
      payload: { rbd, appId, angebotsId },
    };
  }

  treuebonusChangedTemp(
    treuebonus: number,
    appId: App,
    angebotsId?: string,
  ): GlobalAction<TreuebonusChanged> {
    return {
      type: ToolbarActions.TREUEBONUS_CHANGED_TEMP,
      payload: { treuebonus, appId, angebotsId },
    };
  }

  rbdMaxUpdated(rbd: Array<RBD>): ToolbarAction<Array<RBD>> {
    return { type: ToolbarActions.RBD_MAX_UPDATED, payload: rbd };
  }

  rabattServiceNachlaesseReceived(
    nachlaesse: Array<NachlaesseForAngebot>,
  ): ToolbarAction<Array<NachlaesseForAngebot>> {
    return { type: ToolbarActions.RABATT_SERVICE_NACHLAESSE_RECEIVED, payload: nachlaesse };
  }
  initRabattServiceNachlaesse(
    nachlaesse: Array<NachlaesseForAngebot>,
  ): ToolbarAction<Array<NachlaesseForAngebot>> {
    return { type: ToolbarActions.RABATT_SERVICE_NACHLAESSE_SUCCESS, payload: nachlaesse };
  }

  rabattService(
    rabatteRequest: Array<TaasNachlassVorteilRequest>,
  ): ToolbarAction<Array<TaasNachlassVorteilRequest>> {
    return { type: ToolbarActions.RABATT_SERVICE, payload: rabatteRequest };
  }

  treuebonusUpdated(rbd: Array<Treuebonus>): ToolbarAction<Array<Treuebonus>> {
    return { type: ToolbarActions.TREUEBONUS_MAX_UPDATED, payload: rbd };
  }

  openfaq(openFaq: boolean): ToolbarAction<boolean> {
    return { type: ToolbarActions.OPEN_FAQ, payload: openFaq };
  }

  kuendigungChanged(
    kuendigung: VorliegendeKuendigungChanged,
  ): ToolbarAction<VorliegendeKuendigungChanged> {
    return { type: ToolbarActions.KUENDIGUNG_CHANGED, payload: kuendigung };
  }

  vnRoleSelectionChanged(vnRole: VnRoleEnum = null): ToolbarAction<VnRoleEnum> {
    return { type: ToolbarActions.VN_ROLE_SELECTION_CHANGED, payload: vnRole };
  }

  treuebonusResetChanged(treuebonusReset: boolean): ToolbarAction<boolean> {
    return {
      type: ToolbarActions.TREUEBONUS_RESET_CHANGED,
      payload: treuebonusReset,
    };
  }

  requestLogout(): ToolbarAction<void> {
    return { type: ToolbarActions.REQUEST_LOGOUT };
  }

  updateNewsNeusteDate(date: string): ToolbarAction<string> {
    return {
      type: ToolbarActions.UPDATE_NEWS_NEUSTE_DATE,
      payload: date,
    };
  }

  updateTariffUpdateLatestDate(date: string): ToolbarAction<string> {
    return {
      type: ToolbarActions.UPDATE_TARIFF_UPDATE_LATEST_DATE,
      payload: date,
    };
  }

  updateNewsNeusteRead(): ToolbarAction<string> {
    return { type: ToolbarActions.UPDATE_NEWS_NEUSTE_READ };
  }

  updateTariffUpdateRead(): ToolbarAction<string> {
    return { type: ToolbarActions.UPDATE_TARIFF_UPDATE_LAST_READ };
  }

  updateCurrentVNRole(value: VnRoleEnum): GlobalAction<VnRoleEnum> {
    return {
      type: ToolbarActions.UPDATE_CURRENT_VN_ROLE,
      payload: value,
    };
  }

  updateVorbelegteVNRole(value: VnRoleEnum): GlobalAction<VnRoleEnum> {
    return {
      type: ToolbarActions.UPDATE_VORBELEGTE_VN_ROLE,
      payload: value,
    };
  }

  initMandatoryNews(newsItems: NewsItem[]): ToolbarAction<NewsItem[]> {
    return {
      type: ToolbarActions.INIT_MANDATORY_NEWS,
      payload: newsItems,
    };
  }

  newsItemClicked(newsItem: NewsItem): ToolbarAction<NewsItem> {
    return {
      type: ToolbarActions.NEWS_ITEM_CLICKED,
      payload: newsItem,
    };
  }

  switchShowKontobewegungen(value: boolean): ToolbarAction<boolean> {
    return {
      type: ToolbarActions.SWITCH_SHOW_KONTOBEWEGUNGEN,
      payload: value,
    };
  }

  changeVorteilsrabattForProdukt(
    produkt: VorteilsrabattProdukt,
    value: number,
  ): ToolbarAction<ChangeVorteilsrabattForProduktPayload> {
    return {
      type: ToolbarActions.CHANGE_VORTEILSRABATT_FOR_PRODUKT,
      payload: { appId: produkt.appId, angebotsId: produkt.angebotsId, value },
    };
  }
  callKontaflex(vermittlernummer: Vermittlernummer): ToolbarAction<Vermittlernummer> {
    return {
      type: ToolbarActions.CALL_KONTAFLEX,
      payload: vermittlernummer,
    };
  }
  kontaflexReceived(kontaflex: AgencyFinanceAccount): ToolbarAction<AgencyFinanceAccount> {
    return {
      type: ToolbarActions.KONTAFLEX_RECEIVED,
      payload: kontaflex,
    };
  }
}
